/**
 * Utilitaires de calcul des heures supplémentaires selon les règles de l'entreprise
 *
 * RÈGLES:
 * - Semaine impaire: 32h (Lundi-Jeudi = 8h/jour, Vendredi non travaillé sauf exception)
 * - Semaine paire: 38h (Lundi-Jeudi = 8h/jour, Vendredi = 6h)
 * - Semaine Grand Déplacement (GD): 35h fixe (Lundi-Jeudi = 8h45/jour)
 *
 * HEURES SUPPLÉMENTAIRES (Lundi-Samedi):
 * - Semaine impaire: à partir de la 33ème heure
 * - Semaine paire: à partir de la 39ème heure
 * - Semaine GD: à partir de la 36ème heure
 * - Jusqu'à la 43ème heure: majorées à 25%
 * - Au-delà de la 44ème heure: majorées à 50%
 *
 * HEURES DIMANCHE: 100% (majorées à 100%)
 */

// Obtenir le numéro de semaine ISO
export const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

// Déterminer si une semaine est paire ou impaire
export const isEvenWeek = (weekNumber: number): boolean => weekNumber % 2 === 0;

// Obtenir les heures théoriques de la semaine
export const getWeeklyTheoreticalHours = (weekNumber: number): number => {
  return isEvenWeek(weekNumber) ? 38 : 32;
};

// Seuil fixe pour les semaines Grand Déplacement
export const GD_OVERTIME_THRESHOLD = 35;

// Obtenir le seuil de déclenchement des heures supplémentaires
export const getOvertimeThreshold = (weekNumber: number, isGrandDeplacement: boolean = false): number => {
  // Semaine GD: seuil fixe de 35h
  if (isGrandDeplacement) {
    return GD_OVERTIME_THRESHOLD;
  }
  // Semaine paire: HS à partir de 39h / Semaine impaire: HS à partir de 33h
  return isEvenWeek(weekNumber) ? 38 : 32;
};

// Obtenir les heures théoriques par jour de la semaine
export const getDailyTheoreticalHours = (weekNumber: number, dayOfWeek: number): number => {
  // dayOfWeek: 0 = Dimanche, 1 = Lundi, ..., 5 = Vendredi, 6 = Samedi

  if (dayOfWeek === 0 || dayOfWeek === 6) return 0; // Samedi et Dimanche = 0h théorique

  if (dayOfWeek === 5) { // Vendredi
    return isEvenWeek(weekNumber) ? 6 : 0; // 6h en semaine paire, 0h en semaine impaire
  }

  return 8; // Lundi à Jeudi = 8h
};

// Interface pour le détail des heures supplémentaires
export interface OvertimeBreakdown {
  regularHours: number;          // Heures normales (dans le cadre contractuel)
  overtime25: number;            // Heures supp majorées à 25% (33/39 à 43h)
  overtime50: number;            // Heures supp majorées à 50% (au-delà de 44h)
  sundayHours: number;           // Heures dimanche (100%)
  totalOvertime: number;         // Total heures supp (hors dimanche)
  weeklyThreshold: number;       // Seuil HS de la semaine (32, 35 ou 38)
  isEvenWeek: boolean;           // Semaine paire ou impaire
  isGrandDeplacement: boolean;   // Semaine avec Grand Déplacement
}

// Interface pour les entrées d'heures par semaine
export interface WeeklyHoursEntry {
  weekNumber: number;
  year: number;
  hoursNormal: number;           // Heures Lundi-Vendredi
  hoursSaturday: number;         // Heures Samedi
  hoursSunday: number;           // Heures Dimanche (hours_major)
  isGrandDeplacement?: boolean;  // Semaine avec Grand Déplacement
}

/**
 * Calcule le détail des heures supplémentaires pour une semaine donnée
 */
export const calculateWeeklyOvertime = (
  weekNumber: number,
  hoursWorkedLuSa: number,       // Heures travaillées Lundi-Samedi
  hoursSunday: number = 0,       // Heures travaillées Dimanche
  isGrandDeplacement: boolean = false,  // Semaine avec Grand Déplacement
  customWeeklyHours?: number | null     // Base horaire spécifique (intérimaires)
): OvertimeBreakdown => {
  const threshold = customWeeklyHours
    ? customWeeklyHours
    : getOvertimeThreshold(weekNumber, isGrandDeplacement);
  const even = isEvenWeek(weekNumber);

  let regularHours = 0;
  let overtime25 = 0;
  let overtime50 = 0;

  if (hoursWorkedLuSa <= threshold) {
    // Pas d'heures supplémentaires
    regularHours = hoursWorkedLuSa;
  } else {
    // On a des heures supplémentaires
    regularHours = threshold;
    const totalOvertime = hoursWorkedLuSa - threshold;

    // Limite pour les 25%: de la 33/39ème à la 43ème heure
    // Soit max 10 ou 4 heures à 25% selon semaine impaire/paire
    // En fait c'est jusqu'à 43h total, donc:
    // - Semaine impaire: 33-43 = 10h à 25%
    // - Semaine paire: 39-43 = 4h à 25% (mais le seuil est 38, donc 38-43 = 5h à 25%)
    // - Semaine GD: 35-43 = 8h à 25%
    const maxAt25 = 43 - threshold; // 11h pour impaire (32->43), 5h pour paire (38->43), 8h pour GD (35->43)

    if (totalOvertime <= maxAt25) {
      overtime25 = totalOvertime;
    } else {
      overtime25 = maxAt25;
      overtime50 = totalOvertime - maxAt25;
    }
  }

  return {
    regularHours,
    overtime25,
    overtime50,
    sundayHours: hoursSunday,
    totalOvertime: overtime25 + overtime50,
    weeklyThreshold: threshold,
    isEvenWeek: even,
    isGrandDeplacement
  };
};

/**
 * Calcule les heures supplémentaires pour plusieurs semaines (période mensuelle)
 */
export const calculateMonthlyOvertime = (
  weeklyEntries: WeeklyHoursEntry[]
): {
  totalRegular: number;
  totalOvertime25: number;
  totalOvertime50: number;
  totalSunday: number;
  totalOvertime: number;
  weeklyDetails: (OvertimeBreakdown & { weekNumber: number; year: number })[];
} => {
  const weeklyDetails: (OvertimeBreakdown & { weekNumber: number; year: number })[] = [];

  let totalRegular = 0;
  let totalOvertime25 = 0;
  let totalOvertime50 = 0;
  let totalSunday = 0;

  for (const entry of weeklyEntries) {
    const hoursLuSa = entry.hoursNormal + entry.hoursSaturday;
    const breakdown = calculateWeeklyOvertime(entry.weekNumber, hoursLuSa, entry.hoursSunday, entry.isGrandDeplacement || false);

    weeklyDetails.push({
      ...breakdown,
      weekNumber: entry.weekNumber,
      year: entry.year
    });

    totalRegular += breakdown.regularHours;
    totalOvertime25 += breakdown.overtime25;
    totalOvertime50 += breakdown.overtime50;
    totalSunday += breakdown.sundayHours;
  }

  return {
    totalRegular,
    totalOvertime25,
    totalOvertime50,
    totalSunday,
    totalOvertime: totalOvertime25 + totalOvertime50,
    weeklyDetails
  };
};

/**
 * Résout la base horaire hebdo effective pour un employé/semaine donné.
 * Priorité : override de la feuille > custom de l'employé > null (règles standard)
 */
export const resolveWeeklyHours = (
  overrideWeeklyHours?: number | null,
  employeeCustomWeeklyHours?: number | null
): number | null => {
  if (overrideWeeklyHours != null) return overrideWeeklyHours;
  if (employeeCustomWeeklyHours != null) return employeeCustomWeeklyHours;
  return null;
};

/**
 * Obtenir le label descriptif pour le type de semaine
 */
export const getWeekTypeLabel = (weekNumber: number): string => {
  return isEvenWeek(weekNumber)
    ? `Semaine paire (38h)`
    : `Semaine impaire (32h)`;
};

/**
 * Obtenir le label court pour le type de semaine
 */
export const getWeekTypeShort = (weekNumber: number): string => {
  return isEvenWeek(weekNumber) ? 'P' : 'I';
};

/**
 * Formater les heures supplémentaires pour affichage
 */
export const formatOvertimeDisplay = (breakdown: OvertimeBreakdown): string => {
  const parts: string[] = [];

  if (breakdown.overtime25 > 0) {
    parts.push(`${breakdown.overtime25.toFixed(2)}h à 25%`);
  }
  if (breakdown.overtime50 > 0) {
    parts.push(`${breakdown.overtime50.toFixed(2)}h à 50%`);
  }
  if (breakdown.sundayHours > 0) {
    parts.push(`${breakdown.sundayHours.toFixed(2)}h dim. (100%)`);
  }

  return parts.length > 0 ? parts.join(' + ') : '-';
};

/**
 * Obtenir la date de début d'une semaine ISO
 */
export const getWeekStartDate = (year: number, week: number): Date => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = new Date(simple);
  if (dow <= 4) ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  else ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  return ISOweekStart;
};

/**
 * Obtenir la date de fin d'une semaine (dimanche)
 */
export const getWeekEndDate = (weekStart: Date): Date => {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  return end;
};

/**
 * Obtenir l'année ISO d'une date (peut différer de l'année calendaire)
 */
export const getISOYear = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
};
